import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Activity, Briefcase, Building2, CalendarPlus, Clock, Edit2, Globe, History, Mail, Phone,
  AlertTriangle, Plus, Ticket, ToggleLeft, ToggleRight, Trash2, UserPlus, Users, X,
} from 'lucide-react';
import { crmAccountsApi, crmContactRolesApi, crmOrgsApi } from '../../api/crm';
import { companiesApi } from '../../api/companies';
import { useCompany } from '../../contexts/CompanyContext';
import { useConfirm } from '../../components/ConfirmDialog';
import { useClassifications } from '../../hooks/useClassifications';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import Modal from '../../components/Modal';
import OrganizationForm from '../../components/crm/OrganizationForm';
import CrmActivityForm from '../../components/crm/CrmActivityForm';
import CrmActivityItem from '../../components/crm/CrmActivityItem';
import ConversationList from '../../components/crm/ConversationList';
import ComposeEmailModal from '../../components/crm/ComposeEmailModal';
import Timeline from '../../components/crm/Timeline';
import AiBrief from '../../components/crm/AiBrief';
import Field from '../../components/crm/Field';
import { formatMoney, relativeTime } from '../../lib/crmInbox';
import type { ClassificationDef, CrmAccountContact, CrmTimelineKind } from '../../types';

const STAFF_ROLES = new Set(['owner', 'admin', 'technician']);

type Tab = 'overview' | 'people' | 'work' | 'deals' | 'tickets' | 'conversations' | 'history';

const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: 'overview', label: 'Overview', icon: Building2 },
  { id: 'people', label: 'People', icon: Users },
  { id: 'work', label: 'Open work', icon: Activity },
  { id: 'deals', label: 'Opportunities', icon: Briefcase },
  { id: 'tickets', label: 'Tickets', icon: Ticket },
  { id: 'conversations', label: 'Conversations', icon: Mail },
  { id: 'history', label: 'History', icon: History },
];

/** A headline number. `tone` colours only what needs a person's attention. */
function Stat({ label, value, sub, tone, onClick }: { label: string; value: string | number; sub?: string; tone?: 'warn' | 'err'; onClick?: () => void }) {
  const body = (
    <div className="card h-full space-y-1">
      <p className="text-xs text-muted">{label}</p>
      <p className={`text-xl font-bold ${tone === 'err' ? 'text-err' : tone === 'warn' ? 'text-warn' : 'text-fg'}`}>{value}</p>
      {sub && <p className="text-[11px] text-muted">{sub}</p>}
    </div>
  );
  return onClick ? <button type="button" onClick={onClick} className="text-left w-full hover:opacity-90">{body}</button> : body;
}

export default function OrganizationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { currentCompany } = useCompany();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;
  const { optionsFor, labelOf } = useClassifications();

  const [tab, setTab] = useState<Tab>('overview');
  const [editing, setEditing] = useState(false);
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [composing, setComposing] = useState(false);
  const [roleFor, setRoleFor] = useState<CrmAccountContact | null>(null);
  const [historyFilter, setHistoryFilter] = useState<CrmTimelineKind | 'all'>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['crm-account', id, cid],
    queryFn: () => crmAccountsApi.overview(id!),
    enabled: !!id && !!cid,
  });
  const { data: members } = useQuery({
    queryKey: ['company-members', cid],
    queryFn: () => companiesApi.getMembers(cid),
    enabled: !!cid,
  });
  const staff = ((members as any[]) || []).filter(m => STAFF_ROLES.has(m.role) && m.isActive !== false);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['crm-account', id] });

  const patchOrg = useMutation({
    mutationFn: (body: Record<string, unknown>) => crmOrgsApi.update(id!, body as any),
    onSuccess: refresh,
    onError: (e: any) => toast.error(e.response?.data?.message ?? 'Could not save'),
  });
  const deleteMutation = useMutation({
    mutationFn: () => crmOrgsApi.delete(id!),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['crm-orgs'] }); navigate('/crm/organizations'); },
  });
  const removeRole = useMutation({
    mutationFn: (roleId: string) => crmContactRolesApi.remove(roleId),
    onSuccess: refresh,
  });

  const handleDelete = async () => {
    if (await confirm({ title: 'Delete organization', message: 'Delete this organization? This cannot be undone.', confirmLabel: 'Delete', danger: true })) {
      deleteMutation.mutate();
    }
  };

  if (isLoading || !data) return <div className="text-muted text-sm">Loading…</div>;
  const { organization: org, contacts, deals, tickets, conversations, openWork, timeline, stats } = data;
  const openDeals = deals.filter(d => d.status === 'open');
  const openTickets = tickets.filter(t => !['resolved', 'closed'].includes(t.statusCategory));
  const statusOptions = optionsFor('account_status');

  // Things a person should act on, computed from the same records the tabs show.
  const attention: { text: string; go: () => void }[] = [];
  if (stats.needsReply > 0) attention.push({ text: `${stats.needsReply} conversation${stats.needsReply === 1 ? '' : 's'} waiting on a reply`, go: () => setTab('conversations') });
  if (stats.overdueWork > 0) attention.push({ text: `${stats.overdueWork} overdue follow-up${stats.overdueWork === 1 ? '' : 's'}`, go: () => setTab('work') });
  if (stats.dealsWithoutNextAction > 0) attention.push({ text: `${stats.dealsWithoutNextAction} open opportunit${stats.dealsWithoutNextAction === 1 ? 'y has' : 'ies have'} no next step`, go: () => setTab('deals') });
  if (!org.ownerId) attention.push({ text: 'Nobody owns this account', go: () => setTab('overview') });

  return (
    <div className="flex flex-col gap-4 pb-10">
      <PageHeader
        title={
          <span className="flex flex-col gap-1">
            <span>{org.name}</span>
            <span className="flex items-center gap-3 text-[13px] font-normal flex-wrap text-muted">
              {org.industry && <span>{org.industry}</span>}
              <span>{labelOf('account_status', org.status) || org.status}</span>
              <span>{org.owner ? `Owned by ${org.owner.name}` : 'Unowned'}</span>
              {org.lastActivityAt && <span>Last activity {relativeTime(org.lastActivityAt)}</span>}
            </span>
          </span>
        }
        actions={
          <>
            <button onClick={() => setComposing(true)} className="btn btn-secondary flex items-center gap-1" title="Email a contact here from a CRM mailbox">
              <Mail className="w-3.5 h-3.5" /> Email
            </button>
            <button onClick={() => setShowActivityForm(true)} className="btn btn-secondary flex items-center gap-1">
              <CalendarPlus className="w-3.5 h-3.5" /> Log / schedule
            </button>
            <button onClick={() => setEditing(true)} className="btn btn-secondary flex items-center gap-1"><Edit2 className="w-3.5 h-3.5" /> Edit</button>
            <button onClick={handleDelete} className="btn btn-danger" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
          </>
        }
      />

      {/* What needs attention — the reason to open an account page at all. */}
      {attention.length > 0 && (
        <div className="card" style={{ borderColor: 'color-mix(in srgb, var(--scpa-status-warning) 45%, transparent)' }}>
          <p className="section-title flex items-center gap-1.5 mb-2"><AlertTriangle className="w-4 h-4 text-warn" /> Needs attention</p>
          <ul className="flex flex-wrap gap-2">
            {attention.map(a => (
              <li key={a.text}>
                <button type="button" onClick={a.go} className="text-sm text-fg underline decoration-dotted underline-offset-4 hover:text-primary">
                  {a.text}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="People" value={stats.contacts} onClick={() => setTab('people')} />
        <Stat label="Open opportunities" value={stats.openDeals} sub={formatMoney(stats.pipelineValue)} onClick={() => setTab('deals')} />
        <Stat label="Won" value={stats.wonDeals} sub={formatMoney(stats.wonValue)} onClick={() => setTab('deals')} />
        <Stat label="Open tickets" value={stats.openTickets} sub={`${stats.totalTickets} all time`} onClick={() => setTab('tickets')} />
        <Stat label="Open work" value={stats.openWork} sub={stats.overdueWork ? `${stats.overdueWork} overdue` : 'nothing overdue'} tone={stats.overdueWork ? 'err' : undefined} onClick={() => setTab('work')} />
      </div>

      <div className="border-b border-line overflow-x-auto">
        <nav className="flex gap-4">
          {TABS.map(({ id: t, label, icon: Icon }) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-fg'}`}
            >
              <Icon className="inline w-3.5 h-3.5 mr-1" />{label}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <AiBrief kind="account" id={id!} label="Catch me up on this account" />
            <div className="card">
              <h3 className="section-title mb-3">Recent history</h3>
              <Timeline events={timeline.slice(0, 12)} empty="Nothing recorded for this account yet." />
              {timeline.length > 12 && (
                <button type="button" className="text-xs text-primary hover:underline mt-3" onClick={() => setTab('history')}>
                  See the full history
                </button>
              )}
            </div>
            {org.notes && (
              <div className="card">
                <h3 className="section-title mb-2">Notes</h3>
                <p className="text-sm text-fg whitespace-pre-wrap">{org.notes}</p>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="card space-y-3">
              <h3 className="section-title">Relationship</h3>
              <Field label="Account owner">
                <select
                  className="select w-full"
                  value={org.ownerId ?? ''}
                  onChange={e => patchOrg.mutate({ ownerId: e.target.value || null })}
                >
                  <option value="">Unowned</option>
                  {staff.map((m: any) => {
                    const uid = m.user?.id ?? m.userId;
                    return <option key={uid} value={uid}>{m.user?.name || m.user?.email}</option>;
                  })}
                </select>
              </Field>
              <Field label="Account status">
                <select className="select w-full" value={org.status} onChange={e => patchOrg.mutate({ status: e.target.value })}>
                  {statusOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </Field>
            </div>

            <div className="card space-y-2">
              <h3 className="section-title">Details</h3>
              {org.website && <a href={org.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline"><Globe className="w-4 h-4" />{org.website.replace(/^https?:\/\//, '')}</a>}
              {org.phone && <span className="flex items-center gap-2 text-sm text-fg"><Phone className="w-4 h-4 text-muted" />{org.phone}</span>}
              {org.domain && <p className="text-xs text-muted">Email domain: {org.domain} — mail from here links to this account automatically.</p>}
              {org.employeeCount ? <p className="text-xs text-muted">{org.employeeCount.toLocaleString()} employees</p> : null}
              {org.annualRevenue ? <p className="text-xs text-muted">Revenue: {formatMoney(org.annualRevenue)}</p> : null}
              {org.createdBy && <p className="text-xs text-muted">Added by {org.createdBy.name}</p>}
            </div>

            <div className="card space-y-2">
              <h3 className="section-title">Portal access</h3>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-fg">Shared ticket access</p>
                  <p className="text-xs text-muted">
                    {(org as any).sharedTicketAccess ? 'Contacts see every ticket for this account' : 'Contacts see only their own tickets'}
                  </p>
                </div>
                <button
                  type="button"
                  className="text-primary"
                  aria-pressed={!!(org as any).sharedTicketAccess}
                  aria-label="Shared ticket access"
                  onClick={() => patchOrg.mutate({ sharedTicketAccess: !(org as any).sharedTicketAccess })}
                >
                  {(org as any).sharedTicketAccess ? <ToggleRight className="w-7 h-7" /> : <ToggleLeft className="w-7 h-7 text-muted" />}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'people' && (
        <div className="space-y-2">
          {contacts.length === 0 ? (
            <EmptyState icon={<Users className="w-8 h-8" />}>Nobody is linked to this account yet. Contacts join it automatically when they email in from its domain.</EmptyState>
          ) : contacts.map(c => (
            <div key={c.id} className="card flex items-start gap-3">
              <span className="w-8 h-8 rounded-full bg-primary-muted flex items-center justify-center text-primary text-sm font-semibold flex-shrink-0">
                {c.firstName[0]}{c.lastName?.[0] ?? ''}
              </span>
              <div className="flex-1 min-w-0">
                <Link to={`/crm/contacts/${c.id}`} className="font-medium text-sm text-fg hover:text-primary">{c.firstName} {c.lastName}</Link>
                <p className="text-xs text-muted">
                  {[c.jobTitle, c.email, c.phone].filter(Boolean).join(' · ')}
                </p>
                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                  {c.roles.map(r => (
                    <span key={r.id} className="badge bg-primary-muted text-primary inline-flex items-center gap-1">
                      {labelOf('contact_role', r.role) || r.role}{r.isPrimary ? ' · primary' : ''}
                      <button type="button" onClick={() => removeRole.mutate(r.id)} aria-label="Remove role"><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                  <button type="button" className="text-xs text-primary hover:underline" onClick={() => setRoleFor(c)}>
                    <UserPlus className="w-3 h-3 inline mr-0.5" />Add role
                  </button>
                </div>
              </div>
              <span className="text-xs font-semibold text-muted capitalize whitespace-nowrap">{c.status}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'work' && (
        <div className="space-y-2">
          <div className="flex justify-end">
            <button onClick={() => setShowActivityForm(true)} className="btn btn-primary flex items-center gap-1.5"><Plus className="w-4 h-4" /> Log / schedule</button>
          </div>
          {openWork.length === 0 ? (
            <EmptyState icon={<Activity className="w-8 h-8" />}>No open calls, meetings or tasks for this account.</EmptyState>
          ) : openWork.map(a => <CrmActivityItem key={a.id} activity={a as any} onUpdated={refresh} />)}
        </div>
      )}

      {tab === 'deals' && (
        <div className="space-y-2">
          {deals.length === 0 ? (
            <EmptyState icon={<Briefcase className="w-8 h-8" />}>No opportunities yet. Start one from a conversation, or on the Deals board.</EmptyState>
          ) : (
            <>
              {openDeals.length > 0 && <p className="text-[11px] font-bold uppercase tracking-wide text-muted">Open</p>}
              {deals.map(d => (
                <Link key={d.id} to={`/crm/deals/${d.id}`} className="card flex items-start justify-between gap-3 hover:bg-row-odd transition-colors">
                  <div className="min-w-0">
                    <p className="font-medium text-sm text-fg">{d.title}</p>
                    <p className="text-xs text-muted flex items-center gap-1.5 flex-wrap">
                      {d.stage && <><span className="w-2 h-2 rounded-full" style={{ backgroundColor: d.stage.color }} />{d.stage.name}</>}
                      <span className="capitalize">· {d.status}</span>
                      {d.productLine && <span>· {d.productLine.name}</span>}
                    </p>
                    {d.status === 'open' && (
                      <p className={`text-xs mt-1 ${d.nextAction ? 'text-fg' : 'text-warn'}`}>
                        {d.nextAction
                          ? `Next: ${d.nextAction}${d.nextActionDate ? ` · ${new Date(d.nextActionDate).toLocaleDateString()}` : ''}`
                          : 'No next step decided'}
                      </p>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-fg whitespace-nowrap">{formatMoney(d.value, d.currency)}</p>
                </Link>
              ))}
            </>
          )}
        </div>
      )}

      {tab === 'tickets' && (
        <div className="space-y-2">
          {tickets.length === 0 ? (
            <EmptyState icon={<Ticket className="w-8 h-8" />}>No tickets from this account.</EmptyState>
          ) : (
            <>
              <p className="text-xs text-muted">{openTickets.length} open of {tickets.length}.</p>
              {tickets.map(t => (
                <Link key={t.id} to={`/tickets/${t.id}`} className="card flex items-center justify-between gap-3 hover:bg-row-odd transition-colors">
                  <div className="min-w-0">
                    <p className="text-sm text-fg truncate">
                      <span className="font-mono text-[11px] text-muted mr-1.5">{t.ticketNumber}</span>{t.title}
                    </p>
                    <p className="text-xs text-muted">
                      Opened {relativeTime(t.createdAt)}{t.assignedTo ? ` · ${t.assignedTo.name}` : ' · unassigned'}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-muted capitalize whitespace-nowrap">{t.status.replace('_', ' ')}</span>
                </Link>
              ))}
            </>
          )}
        </div>
      )}

      {tab === 'conversations' && (
        <ConversationList conversations={conversations} empty="No email conversations with this account yet." />
      )}

      {tab === 'history' && (
        <div className="card">
          <Timeline
            events={timeline}
            filterable
            active={historyFilter}
            onFilter={setHistoryFilter}
            empty="Nothing recorded for this account yet."
          />
        </div>
      )}

      {/* ── Modals ── */}
      {editing && (
        <OrganizationForm
          org={org as any}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); refresh(); }}
        />
      )}

      {showActivityForm && (
        <CrmActivityForm
          defaults={{ organizationId: id }}
          onClose={() => setShowActivityForm(false)}
          onSaved={() => { setShowActivityForm(false); refresh(); }}
        />
      )}

      <ComposeEmailModal
        open={composing}
        onClose={() => setComposing(false)}
        defaults={{ to: contacts.find(c => c.email)?.email ?? '', organizationId: id }}
      />

      {roleFor && (
        <RoleModal
          contact={roleFor}
          organizationId={id!}
          options={optionsFor('contact_role')}
          onClose={() => setRoleFor(null)}
          onSaved={() => { setRoleFor(null); refresh(); }}
        />
      )}
    </div>
  );
}

/** Give a person a role on this account. The list comes from Company
 *  Settings → Classification, so it is the tenant's vocabulary, not ours. */
function RoleModal({ contact, organizationId, options, onClose, onSaved }: {
  contact: CrmAccountContact;
  organizationId: string;
  options: ClassificationDef[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [role, setRole] = useState(options[0]?.key ?? '');
  const [isPrimary, setIsPrimary] = useState(false);
  const [error, setError] = useState('');

  const save = useMutation({
    mutationFn: () => crmContactRolesApi.create({ contactId: contact.id, organizationId, role, isPrimary }),
    onSuccess: onSaved,
    onError: (e: any) => setError(e.response?.data?.message ?? 'Could not add the role'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={`${contact.firstName} ${contact.lastName ?? ''}`.trim() + ' — add a role'}
      busy={save.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!role || save.isPending} onClick={() => save.mutate()}>Add role</button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Contact role" required>
          <select className="select w-full" value={role} onChange={e => setRole(e.target.value)}>
            {options.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        </Field>
        <label className="flex items-center gap-2 text-sm text-fg">
          <input type="checkbox" checked={isPrimary} onChange={e => setIsPrimary(e.target.checked)} />
          Primary contact for this account
          <span className="text-xs text-muted">— only one person can hold this</span>
        </label>
        {error && <p className="text-sm text-err">{error}</p>}
      </div>
    </Modal>
  );
}
